<template>
  <div
    class="select-box-holder width-10"
    v-validate="errors"
    v-has-value="value"
  >
    <div class="input-box">
      <div class="input-holder">
        <div
          class="wrapper"
          v-has-value="value"
          ref="datepicker"
        >
          <!-- Desktop datepicker-box -->
          <date-picker
            type="date"
            ref="input"
            v-if="!isMobile"
            :value="desktopDate"
            :formatter="formatObject"
            :default-value="initialView"
            :disabled-date="disableRange"
            :disabled="disabled"
            :lang="lang"
            @input="desktopSetter($event)"
            @close="$emit('onBlur')"
          ></date-picker>

          <!-- Mobile datepicker-box -->
          <input
            type="date"
            name="date"
            v-if="isMobile && !useTextInputInMobile"
            :value="mobileDate"
            :disabled="disabled ? disabled : false"
            @blur="$emit('onBlur')"
            @input="mobileSetter($event.target.value)"
          >

          <!-- Text input datepicker -->
          <input
            ref="text-input"
            type="text"
            name="date"
            v-if="isMobile && useTextInputInMobile"
            :value="textInputDate"
            :disabled="disabled ? disabled : false"
            @blur="textInputOnBlur"
            @input="textInputSetter"
          >
        </div>

        <!-- Input label -->
        <slot></slot>

        <!-- Calendar icon -->
        <i v-if="!isMobile || (isMobile && !useTextInputInMobile)" @click="inputFocus()" class="icon icon--calendar-black"></i>

        <!-- Reset value icon -->
        <i v-if="!disabled" @click="$emit('input', null)" class="icon icon--cross-gray"></i>
      </div>

      <!-- Tooltip -->
      <div v-if="!isTooltip" class="tooltip"></div>
      <tooltip v-if="isTooltip" ref="tooltip">
        <slot name="tooltip"></slot>
        <template slot="button-cancel" v-if="!!this.$slots['button-cancel']">
          <slot name="button-cancel"></slot>
        </template>
      </tooltip>
    </div>

    <!-- Validations -->
    <div class="validation-output">
      <slot name="validations"></slot>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import { Tooltip } from 'union_components';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import {
  formatDateForKotlin, makeDateFromKotlin, makeDateFromNativeInput, makeDateForNativeInput,
  makeDateStringFromKotlin,
} from 'union_kotlin';

export default {
  name: 'DatepickerBox',
  props: {
    errors: {
      required: true,
      type: Array,
    },
    value: {
      required: true,
      validator: (prop) => typeof prop === 'object' || prop === null, // Kotlinvosky datum
    },
    initialView: {
      required: false,
      type: Date,
      default: () => new Date(),
    },
    disabled: {
      required: false,
      type: Boolean,
      default: () => false,
    },
    minDate: {
      required: false,
      type: Date,
    },
    maxDate: {
      required: false,
      type: Date,
    },
    format: {
      required: false,
      type: String,
      default: () => 'L',
    },
    locale: {
      required: false,
      type: String,
      default: () => 'sk',
    },
    useTextInputInMobile: {
      required: false,
      type: Boolean,
      default: () => false,
    }
  },
  components: {
    DatePicker, Tooltip,
  },
  computed: {
    /*
    * Vrati true ak datum ma tooltip
    * */
    isTooltip() {
      return !!this.$slots.tooltip;
    },
    /*
    * Vrati true ak je to mobilne zariadenie Android|iOS|Windows Phone
    * */
    isMobile() {
      return [
        /Android/i, /webOS/i, /iPhone/i, /iPad/i,
        /iPod/i, /BlackBerry/i, /Windows Phone/i,
      ].some((toMatchItem) => navigator.userAgent.match(toMatchItem));
    },
    /*
    * Konvertuje kotlinovsky datum do JS datumu,
    * ak nie je zadany datum vrati null
    * */
    desktopDate() {
      if (!this.value) return null;
      return makeDateFromKotlin(this.value);
    },
    /*
    * Konvertuje kotlinovsky datum do stringu,
    * ak nie je zadany datum vrati null
    * */
    mobileDate() {
      if (!this.value) return null;
      const jsDate = makeDateFromKotlin(this.value);
      return makeDateForNativeInput(jsDate);
    },
    /*
    * Zformatuje kotlinovsky datum do SVK stringu
    * pre textovy datepicker
    * */
    textInputDate() {
      if (!this.value) return null;
      return makeDateStringFromKotlin(this.value);
    },
    /**
     * Formatuje datum v desktopovom datepickri
     * Dátum môze mat dlhy a kratky tvar.
     *
     * Kratky tvar = 12.1.2020
     * Dlhy tvar = 12. dec 2020
     *
     * @prop Format = 'll' || 'L'
     * */
    formatObject() {
      let momentLocale = this.locale;
      if (momentLocale === 'en') momentLocale = 'en-gb';
      moment.locale(momentLocale);

      // Dlhy tvar
      if (this.format === 'll') {
        return {
          stringify: (date) => (date ? moment(date).format('ll') : ''),
          parse: (value) => {
            if (!!value && typeof value === 'string' && value.split('.').length === 3) {
              const [day, month, year] = value.split('.');
              const date = new Date(`${month}/${day}/${year}`);
              if (Object.prototype.toString.call(date) === '[object Date]') { // it is a date
                if (!Number.isNaN(date.getTime())) { // it is valid date
                  return date;
                }
              }
            }
            return null;
          },
        };
      }
      // Kratky tvar
      return {
        stringify: (date) => (date ? moment(date).format('L') : ''),
        parse: (value) => (value ? moment(value, 'L').toDate() : null),
      };
    },
    /*
    * Prepianie medzi SVK a ANJ jazykom
    * */
    lang() {
      if (this.locale === 'en') return 'en';
      return {
        yearFormat: 'YYYY',
        monthFormat: 'MMM',
        monthBeforeYear: true,
        formatLocale: {
          months: ['Január', 'Február', 'Marec', 'Apríl', 'Máj', 'Jún', 'Júl', 'August', 'September', 'Október', 'November', 'December'],
          monthsShort: ['Jan', 'Feb', 'Mar', 'Apr', 'Máj', 'Jún', 'Júl', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'],
          weekdays: ['Nedeľa', 'Pondleok', 'Utorok', 'Streda', 'Štvrtok', 'Piatok', 'Sobota'],
          weekdaysShort: ['Ned.', 'Pon.', 'Uto.', 'Str.', 'Štv.', 'Pia.', 'Sob.'],
          weekdaysMin: ['Ne', 'Po', 'Ut', 'St', 'Št', 'Pi', 'So'],
          days: ['Ne', 'Po', 'Ut', 'St', 'Št', 'Pi', 'So'],
          firstDayOfWeek: 1,
          firstWeekContainsDate: 7,
        },
      };
    },
  },
  methods: {
    /*
    * Metoda vrati zkonvertovany js datum zo stringu alebo null
    * */
    getDateFromString(str) {
      if (/[0-9]{1,2}.[0-9]{1,2}.[1-9]{1}[0-9]{3}/.test(str)) {
        const [day, month, year] = str.split('.');
        const jsDate = new Date(`${month}/${day}/${year}`);
        if (!isNaN(jsDate.getTime())) {
          return jsDate;
        }
      }
      return null;
    },
    /*
    * Metoda zvaliduje string ak je validny zkonvertuje
    * ho do kotlinovskeho datumu, ktory potom emitne.
    * */
    textInputSetter(keyboardEvent) {
      const { value } = keyboardEvent.target;
      if (this.getDateFromString(value)) {
        const JSDate = this.getDateFromString(value);
        this.$emit('input', formatDateForKotlin(JSDate));
      }
    },
    /*
    * Metoda sa spusta na blur z text inputu ak hodnota v
    * inpute nie je validna vymaze ju.
    * */
    textInputOnBlur(e) {
      if (!this.getDateFromString(e.target.value)) {
        const inputEl = this.$refs['text-input'];
        inputEl.value = "";
        this.$emit('input', null);
      }
      this.$emit('onBlur');
    },
    /*
    * Premeni string na kotlinovsky datum a emitne hodnotu
    * tak aby fungoval two way binding ($emit('input'))
    * */
    mobileSetter(date) {
      if (!date) this.$emit('input', null);
      else {
        const jsDate = makeDateFromNativeInput(date);
        this.$emit('input', formatDateForKotlin(jsDate));
      }
    },
    /*
    * Premeni string na kotlinovsky datum a emitne hodnotu
    * tak aby fungoval two way binding ($emit('input'))
    * */
    desktopSetter(date) {
      this.$emit('input', formatDateForKotlin(date));
    },
    /*
    * Prechadza datumamy a pozera ci dany datum je v rozmedzi.
    * */
    disableRange(date) {
      let condition1 = false;
      let condition2 = false;
      if (this.minDate) condition1 = date < this.minDate;
      if (this.maxDate) condition2 = date > this.maxDate;
      return condition1 || condition2;
    },
    /*
    * Focusne do inputu a zobrazi datepicker
    * */
    inputFocus() {
      const { input } = this.$refs;
      if (input) input.focus();
    },
    /*
    * Otvori tooltip
    * */
    openTooltip() {
      const { tooltip } = this.$refs;
      if (tooltip) tooltip.handleTooltip(true);
    },
  },
};
</script>
